import { motion, type Variants } from 'framer-motion';
import { MessageSquare, PenTool, Code, Rocket } from 'lucide-react';

const steps = [
  {
    number: '01',
    icon: MessageSquare,
    title: 'Discovery',
    description: 'We start by understanding your business, goals, target audience, and what makes you unique.',
  },
  {
    number: '02',
    icon: PenTool,
    title: 'Design',
    description: 'Our designers craft visual concepts that align with your brand and communicate your message clearly.',
  },
  {
    number: '03',
    icon: Code,
    title: 'Development',
    description: 'We build your website with clean, modern code — fast, responsive, and built to scale.',
  },
  {
    number: '04',
    icon: Rocket,
    title: 'Launch',
    description: 'After thorough testing, we deploy your site and provide ongoing support for continued success.',
  },
];

const container: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.12 } },
};

const item: Variants = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const Process = () => {
  return (
    <section id="process" className="py-24 bg-white dark:bg-dark transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-highlight font-medium text-sm uppercase tracking-wider">How We Work</span>
          <h2 className="text-4xl md:text-5xl font-display font-bold text-primary dark:text-white mt-3 mb-4">
            A Process Built for Success
          </h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto text-lg">
            From initial concept to final launch, we follow a proven process that delivers exceptional results.
          </p>
        </motion.div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-100px' }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              variants={item}
              className="relative p-8 rounded-2xl dark:bg-secondary/30 dark:border dark:border-white/5"
            >
              <div className="text-6xl font-display font-bold text-gray-100 dark:text-white/5 absolute top-4 right-4">
                {step.number}
              </div>
              <div className="relative z-10">
                <div className="w-14 h-14 bg-primary dark:bg-highlight rounded-xl flex items-center justify-center mb-6">
                  <step.icon className="text-white" size={28} />
                </div>
                <h3 className="text-xl font-bold text-primary dark:text-white mb-3">{step.title}</h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{step.description}</p>
              </div>
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gray-200 dark:bg-white/10" />
              )}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Process;
